/**
 * separation feature — React Query hooks + fetch helpers.
 *
 * 백엔드 API (src/jeeum/api/separation.py, tab.py, recording.py) 를 래핑.
 * 알고리즘 변경 없이 기존 fetch 호출을 RQ 쿼리로 바꾼다.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { http, httpJson } from '@/lib/http';
import { tokenColor } from '@/lib/tokens';
import { useAuthStore } from '@/features/auth';
import { awaitPendingSharedStateSave } from './hooks/useSharedStateAutosave';

// ---- 타입 ----

export interface SeparationProgress {
  id: number;
  file_name: string;
  file_path?: string;
  status: 'pending' | 'separating' | 'refining' | 'completed' | 'failed';
  progress?: number;
  current_phase?:
    | 'queued'
    | 'download_source'
    | 'extract_audio'
    | 'separate_main'
    | 'guitar_split'
    | 'extract_metadata'
    | 'compute_waveform'
    | 'prepare_studio_audio'
    | 'prepare_analysis_cache'
    | 'completed'
    | 'failed'
    | null;
  current_step?: string | null;
  error?: string | null;
  failure_code?: string | null;
  failure_detail?: string | null;
  bpm?: number | null;
  key?: string | null;
  duration_sec?: number | null;
  stem_count?: number | null;
  estimated_total_seconds?: number | null;
  estimated_remaining_seconds?: number | null;
  estimated_remaining_min_seconds?: number | null;
  estimated_remaining_max_seconds?: number | null;
  estimated_completion_at?: string | null;
  estimate_confidence?: 'low' | 'medium' | 'high' | null;
  beat_times?: number[] | null;
  downbeat_times?: number[] | null;
  created_at?: string;
  is_optimistic?: boolean;
}

export interface StemInfo {
  name: string;
  label: string;
  color: string;
  audio_url: string;
}

// 스템 트랙 색상의 SSOT 는 tokens.css 의 --stem-*-fg 도메인 토큰이다.
// 백엔드 STEM_DISPLAY 의 hex 는 2026-05-07 oxblood refresh 이전 팔레트가
// 남아 있으므로 표시 직전에 토큰 값으로 덮어쓴다. 토큰이 없는 스템만
// 백엔드 색을 그대로 둔다 (런타임 미정의 토큰도 동일하게 fallback).
const STEM_COLOR_TOKENS: Record<string, string> = {
  vocals: 'stem-vocals-fg',
  instrumental: 'stem-instrumental-fg',
  drums: 'stem-drums-fg',
  bass: 'stem-bass-fg',
  guitar: 'stem-guitar-fg',
  lead_guitar: 'stem-lead-guitar-fg',
  rhythm_guitar: 'stem-rhythm-guitar-fg',
  electric_guitar: 'stem-electric-guitar-fg',
  acoustic_guitar: 'stem-acoustic-guitar-fg',
  piano: 'stem-piano-fg',
  other: 'stem-other-fg',
  original: 'stem-original-fg',
};

export function withTokenStemColors(result: SeparationResult): SeparationResult {
  if (!result?.stems?.length) return result;
  return {
    ...result,
    stems: result.stems.map((stem) => {
      const tokenName = STEM_COLOR_TOKENS[stem.name];
      const color = tokenName ? tokenColor(tokenName) : '';
      return color ? { ...stem, color } : stem;
    }),
  };
}

export interface SeparationResult extends SeparationProgress {
  preview_audio_url: string | null;
  stems: StemInfo[];
  selected_stems?: string[] | null;
  guitar_mode?: 'single' | 'electric_acoustic' | 'lead_rhythm' | null;
  guitar_split_status?: 'split' | 'fallback' | null;
  guitar_split_message?: string | null;
}

export interface WaveformData {
  peaks: number[];
  duration?: number;
}

export interface ChordItem {
  time: number;
  duration: number;
  label: string;
  confidence?: number | null;
  derived_from_consensus?: boolean | null;
}

export interface MeasureChordSegment {
  start: number;
  end: number;
  label: string;
  confidence?: number | null;
  derived_from_consensus?: boolean | null;
}

export interface MeasureChordItem {
  measure_index: number;
  start: number;
  end: number;
  section_label?: string | null;
  segments: MeasureChordSegment[];
}

export interface LyricItem {
  time: number;
  duration: number;
  text: string;
}

export interface LyricAnnotationItem {
  time: number;
  duration: number;
  text: string;
  reading?: string | null;
  meaning?: string | null;
}

export interface LyricAnnotationLayer {
  source_language: string;
  target_language: string;
  source: 'machine' | 'user';
  items: LyricAnnotationItem[];
}

export interface SectionItem {
  start: number;
  end: number;
  label: string;
}

export type LyricsCorrectionStatus =
  | 'pending'
  | 'running'
  | 'done'
  | 'failed'
  | 'skipped';

export type LyricAnnotationStatus =
  | 'empty'
  | 'pending'
  | 'running'
  | 'done'
  | 'failed'
  | 'skipped';

export type SectionsStatus = 'pending' | 'running' | 'done' | 'failed';
export type SharedTabRenderer = 'source' | 'osmd' | 'alphatab';

export interface AnalysisData {
  chords?: ChordItem[];
  measure_chords?: MeasureChordItem[];
  chord_status?: 'pending' | 'running' | 'ready' | 'failed' | null;
  chord_revision_id?: number | null;
  chord_source?: 'legacy' | 'machine' | 'user' | null;
  chord_vocabulary?: string | null;
  is_user_edited?: boolean;
  can_edit?: boolean;
  lyrics?: LyricItem[];
  lyrics_raw?: LyricItem[];
  lyrics_corrected?: LyricItem[];
  lyric_annotations?: LyricAnnotationLayer | null;
  sections?: SectionItem[];
  lyrics_correction_status?: LyricsCorrectionStatus | null;
  lyrics_correction_changed?: boolean | null;
  lyric_annotation_status?: LyricAnnotationStatus | null;
  sections_status?: SectionsStatus | null;
}

export interface LyricAnnotationGenerateResponse {
  ok: boolean;
  lyric_annotation_status: LyricAnnotationStatus;
  lyric_annotations?: LyricAnnotationLayer | null;
}

export interface LyricsCorrectionRetryResponse {
  ok: boolean;
  lyrics_correction_status: LyricsCorrectionStatus;
  started: boolean;
}

export interface ChordRevision {
  id: number;
  separation_id: number;
  parent_revision_id?: number | null;
  created_by_user_id?: number | null;
  source: 'legacy' | 'machine' | 'user';
  status: 'pending' | 'running' | 'ready' | 'failed';
  vocabulary: string;
  model_name?: string | null;
  model_version?: string | null;
  is_published: boolean;
  is_user_edited: boolean;
  chords: ChordItem[];
  measure_chords: MeasureChordItem[];
  metadata?: Record<string, unknown>;
  error?: string | null;
}

export interface ChordRevisionOperation {
  type: 'relabel' | 'split' | 'merge' | 'revert_segment' | 'revert_revision';
  measure_index?: number | null;
  segment_index?: number | null;
  label?: string | null;
  direction?: 'previous' | 'next' | null;
  split_time?: number | null;
  apply_to_matching_sections?: boolean;
}

export interface SharedTrackSettings {
  volume: number;
  pan: number;
  muted: boolean;
}

export interface SharedStudioState {
  loop_start_sec: number | null;
  loop_end_sec: number | null;
  bpm_ratio: number;
  pitch_semitones: number;
  selected_tab_id: number | null;
  selected_tab_renderer: SharedTabRenderer | null;
  track_settings: Record<string, SharedTrackSettings>;
  updated_at?: string | null;
}

export interface SeparationShareStatus {
  is_active: boolean;
  share_url: string | null;
  expires_at: string | null;
  shared_state: SharedStudioState;
}

export interface ShareStudioResponse {
  separation_id: number;
  title: string;
  expires_at: string;
  owner_name: string | null;
  shared_state: SharedStudioState;
}

export interface TabDetail {
  id: number;
  stem_name: string;
  status: 'pending' | 'running' | 'generating' | 'completed' | 'failed';
  progress?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chart_data?: any;
  error?: string | null;
  failure_code?: string | null;
  failure_detail?: string | null;
}

export interface TabTimingEvent {
  event_index: number;
  measure_index: number;
  offset_divisions: number;
  duration_divisions: number;
  start_sec: number;
  end_sec: number;
  note_count: number;
}

export interface TabTimingMeasure {
  measure_index: number;
  /** Imported scores use the timing map's common divisions-per-quarter unit. */
  start_divisions?: number;
  divisions_per_measure?: number;
  start_sec: number;
  end_sec: number;
  event_start_index: number | null;
  event_end_index: number | null;
}

export interface TabTimingMap {
  kind: string;
  bpm: number;
  beats_per_measure: number;
  divisions_per_quarter: number;
  divisions_per_measure: number;
  event_count: number;
  measure_count: number;
  events: TabTimingEvent[];
  measures: TabTimingMeasure[];
}

export interface RecordingTake {
  id: number;
  separation_id: number;
  start_time: number;
  end_time: number;
  is_loop: boolean;
  created_at?: string;
}

export interface StemSelection {
  vocals: boolean;
  drums: boolean;
  bass: boolean;
  piano: boolean;
  guitar: boolean;
  other: boolean;
}

export type SeparationEngineName = 'htdemucs_6s' | 'bs_roformer_4s';
export type GuitarMode = 'single' | 'electric_acoustic' | 'lead_rhythm';

export interface EngineInfo {
  name: SeparationEngineName;
  label: string;
  description: string;
  supported_stems: Array<keyof StemSelection>;
  supports_guitar_mode: boolean;
  is_default: boolean;
  available: boolean;
  unavailable_reason?: string | null;
}

// ---- Query keys ----

export const qk = {
  all: ['separation'] as const,
  engines: () => [...qk.all, 'engines'] as const,
  list: () => [...qk.all, 'list'] as const,
  detail: (id: number) => [...qk.all, 'detail', id] as const,
  status: (id: number) => [...qk.all, 'status', id] as const,
  analysis: (id: number) => [...qk.all, 'analysis', id] as const,
  sections: (id: number) => [...qk.all, 'sections', id] as const,
  tabs: (id: number) => [...qk.all, 'tabs', id] as const,
  takes: (sepId: number) => [...qk.all, 'takes', sepId] as const,
  share: (id: number) => [...qk.all, 'share', id] as const,
  sharedState: (id: number) => [...qk.all, 'sharedState', id] as const,
  chordRevisions: (id: number) => [...qk.all, 'chordRevisions', id] as const,
};

// ---- Queries ----

export function useEngines() {
  return useQuery<EngineInfo[]>({
    queryKey: qk.engines(),
    queryFn: () => httpJson.get<EngineInfo[]>('/api/separation/engines'),
    staleTime: 60_000,
  });
}

export function separationListRefetchInterval(
  data: SeparationProgress[] | undefined,
): number | false {
  return data?.some(
    (item) => item.status !== 'completed' && item.status !== 'failed',
  )
    ? 3000
    : false;
}

export function useSeparationList() {
  return useQuery<SeparationProgress[]>({
    queryKey: qk.list(),
    queryFn: () => httpJson.get<SeparationProgress[]>('/api/separation/list'),
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchIntervalInBackground: true,
    refetchInterval: (query) =>
      separationListRefetchInterval(query.state.data),
  });
}

export function useSeparationStatus(id: number | null, enabled = true) {
  return useQuery<SeparationProgress>({
    queryKey: id != null ? qk.status(id) : ['separation', 'status', 'none'],
    queryFn: () => httpJson.get<SeparationProgress>(`/api/separation/status/${id}`),
    enabled: enabled && id != null,
    refetchInterval: (q) => {
      const data = q.state.data as SeparationProgress | undefined;
      if (!data) return 2000;
      if (data.status === 'completed' || data.status === 'failed') return false;
      return 2000;
    },
  });
}

export function useSeparationDetail(
  id: number | null,
  options?: Omit<UseQueryOptions<SeparationResult>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<SeparationResult>({
    queryKey: id != null ? qk.detail(id) : ['separation', 'detail', 'none'],
    queryFn: () => httpJson.get<SeparationResult>(`/api/separation/${id}`).then(withTokenStemColors),
    enabled: id != null,
    ...options,
  });
}

export function useAnalysis(id: number | null, options?: { enabled?: boolean }) {
  return useQuery<AnalysisData>({
    queryKey: id != null ? qk.analysis(id) : ['separation', 'analysis', 'none'],
    queryFn: () => httpJson.get<AnalysisData>(`/api/separation/analysis/${id}`),
    enabled: (options?.enabled ?? true) && id != null,
    refetchInterval: (q) => {
      const data = q.state.data as AnalysisData | undefined;
      const lyricsStatus = data?.lyrics_correction_status;
      const lyricAnnotationStatus = data?.lyric_annotation_status;
      const sectionsStatus = data?.sections_status;
      return (
        data?.chord_status === 'pending' ||
        data?.chord_status === 'running' ||
        lyricsStatus === 'pending' ||
        lyricsStatus === 'running' ||
        lyricAnnotationStatus === 'pending' ||
        lyricAnnotationStatus === 'running' ||
        sectionsStatus === 'pending' ||
        sectionsStatus === 'running'
      )
        ? 5000
        : false;
    },
  });
}

export function useSections(
  id: number | null,
  status?: SectionsStatus | null,
  options?: { enabled?: boolean },
) {
  return useQuery<SectionItem[]>({
    queryKey: id != null ? qk.sections(id) : ['separation', 'sections', 'none'],
    queryFn: () => httpJson.get<SectionItem[]>(`/api/separation/sections/${id}`),
    enabled: (options?.enabled ?? true) && id != null,
    refetchInterval:
      status === 'pending' || status === 'running' ? 5000 : false,
  });
}

export function useTabs(id: number | null, options?: { enabled?: boolean }) {
  return useQuery<TabDetail[]>({
    queryKey: id != null ? qk.tabs(id) : ['separation', 'tabs', 'none'],
    queryFn: () => httpJson.get<TabDetail[]>(`/api/separation/${id}/tab`),
    enabled: (options?.enabled ?? true) && id != null,
  });
}

export function useDemoSeparationDetail(options?: { enabled?: boolean }) {
  return useQuery<SeparationResult>({
    queryKey: ['demo', 'studio', 'detail'],
    queryFn: () => httpJson.get<SeparationResult>('/api/demo/studio/detail').then(withTokenStemColors),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useDemoAnalysis(options?: { enabled?: boolean }) {
  return useQuery<AnalysisData>({
    queryKey: ['demo', 'studio', 'analysis'],
    queryFn: () => httpJson.get<AnalysisData>('/api/demo/studio/analysis'),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useDemoSections(
  status?: SectionsStatus | null,
  options?: { enabled?: boolean },
) {
  return useQuery<SectionItem[]>({
    queryKey: ['demo', 'studio', 'sections'],
    queryFn: () => httpJson.get<SectionItem[]>('/api/demo/studio/sections'),
    enabled: options?.enabled ?? true,
    refetchInterval:
      status === 'pending' || status === 'running' ? 5000 : false,
  });
}

export function useDemoTabs(options?: { enabled?: boolean }) {
  return useQuery<TabDetail[]>({
    queryKey: ['demo', 'studio', 'tabs'],
    queryFn: () => httpJson.get<TabDetail[]>('/api/demo/studio/tabs'),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useShareStudio(token: string | null, options?: { enabled?: boolean }) {
  return useQuery<ShareStudioResponse>({
    queryKey: token ? ['share', token, 'studio'] : ['share', 'studio', 'none'],
    queryFn: () => httpJson.get<ShareStudioResponse>(`/api/share/${token}/studio`),
    enabled: (options?.enabled ?? true) && Boolean(token),
    staleTime: 60_000,
    retry: false,
  });
}

export function useShareSeparationDetail(token: string | null, options?: { enabled?: boolean }) {
  return useQuery<SeparationResult>({
    queryKey: token ? ['share', token, 'detail'] : ['share', 'detail', 'none'],
    queryFn: () => httpJson.get<SeparationResult>(`/api/share/${token}/detail`).then(withTokenStemColors),
    enabled: (options?.enabled ?? true) && Boolean(token),
    staleTime: 60_000,
    retry: false,
  });
}

export function useShareAnalysis(token: string | null, options?: { enabled?: boolean }) {
  return useQuery<AnalysisData>({
    queryKey: token ? ['share', token, 'analysis'] : ['share', 'analysis', 'none'],
    queryFn: () => httpJson.get<AnalysisData>(`/api/share/${token}/analysis`),
    enabled: (options?.enabled ?? true) && Boolean(token),
    staleTime: 60_000,
    retry: false,
  });
}

export function useShareSections(
  token: string | null,
  status?: SectionsStatus | null,
  options?: { enabled?: boolean },
) {
  return useQuery<SectionItem[]>({
    queryKey: token ? ['share', token, 'sections'] : ['share', 'sections', 'none'],
    queryFn: () => httpJson.get<SectionItem[]>(`/api/share/${token}/sections`),
    enabled: (options?.enabled ?? true) && Boolean(token),
    refetchInterval: status === 'pending' || status === 'running' ? 5000 : false,
    retry: false,
  });
}

export function useShareTabs(token: string | null, options?: { enabled?: boolean }) {
  return useQuery<TabDetail[]>({
    queryKey: token ? ['share', token, 'tabs'] : ['share', 'tabs', 'none'],
    queryFn: () => httpJson.get<TabDetail[]>(`/api/share/${token}/tabs`),
    enabled: (options?.enabled ?? true) && Boolean(token),
    staleTime: 60_000,
    retry: false,
  });
}

export function useShareStatus(sepId: number | null, options?: { enabled?: boolean }) {
  return useQuery<SeparationShareStatus>({
    queryKey: sepId != null ? qk.share(sepId) : ['separation', 'share', 'none'],
    queryFn: async () => {
      if (sepId != null) await awaitPendingSharedStateSave(sepId);
      return httpJson.get<SeparationShareStatus>(`/api/separation/${sepId}/share`);
    },
    // A fresh cache can predate a detached PUT from the room just left.
    refetchOnMount: 'always',
    enabled: (options?.enabled ?? true) && sepId != null,
  });
}

export function useChordRevisions(sepId: number | null, options?: { enabled?: boolean }) {
  return useQuery<ChordRevision[]>({
    queryKey: sepId != null ? qk.chordRevisions(sepId) : ['separation', 'chordRevisions', 'none'],
    queryFn: () => httpJson.get<ChordRevision[]>(`/api/separation/${sepId}/chords/revisions`),
    enabled: (options?.enabled ?? true) && sepId != null,
  });
}

export function useTakes(sepId: number | null) {
  return useQuery<RecordingTake[]>({
    queryKey: sepId != null ? qk.takes(sepId) : ['separation', 'takes', 'none'],
    queryFn: () => httpJson.get<RecordingTake[]>(`/api/recording/takes/${sepId}`),
    enabled: sepId != null,
  });
}

export async function fetchWaveform(
  sepId: number,
  stemName: string,
): Promise<WaveformData> {
  return httpJson.get<WaveformData>(`/api/separation/waveform/${sepId}/${stemName}`);
}

export async function fetchDemoWaveform(stemName: string): Promise<WaveformData> {
  return httpJson.get<WaveformData>(`/api/demo/studio/waveform/${stemName}`);
}

export async function fetchShareWaveform(
  token: string,
  stemName: string,
): Promise<WaveformData> {
  return httpJson.get<WaveformData>(`/api/share/${token}/waveform/${stemName}`);
}

export async function fetchTabDetail(
  sepId: number,
  tabId: number,
  signal?: AbortSignal,
): Promise<TabDetail> {
  return httpJson.get<TabDetail>(
    `/api/separation/${sepId}/tab/${tabId}`,
    { signal },
  );
}

export async function fetchTabTiming(sepId: number, tabId: number): Promise<TabTimingMap> {
  return httpJson.get<TabTimingMap>(`/api/separation/${sepId}/tab/${tabId}/timing`);
}

export async function fetchDemoTabTiming(tabId: number): Promise<TabTimingMap> {
  return httpJson.get<TabTimingMap>(`/api/demo/studio/tab/${tabId}/timing`);
}

export async function fetchShareTabTiming(token: string, tabId: number): Promise<TabTimingMap> {
  return httpJson.get<TabTimingMap>(`/api/share/${token}/tab/${tabId}/timing`);
}

// ---- Mutations ----

export interface UploadAndSeparateArgs {
  file: File;
  engine: SeparationEngineName;
  stems: string[];
  guitarMode: GuitarMode;
}

interface UploadAndSeparateContext {
  pendingReservationId: string;
  optimisticSeparationId: number;
}

export interface StartYouTubeSeparationArgs {
  url: string;
  engine: SeparationEngineName;
  stems: string[];
  guitarMode: GuitarMode;
}

function createPendingReservationId(): string {
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createOptimisticSeparationId(): number {
  return -(Date.now() + Math.floor(Math.random() * 1000));
}

function buildOptimisticSeparation(fileName: string): SeparationProgress {
  return {
    id: createOptimisticSeparationId(),
    file_name: fileName,
    status: 'pending',
    progress: 0,
    current_phase: 'queued',
    created_at: new Date().toISOString(),
    is_optimistic: true,
  };
}

function prependOptimisticSeparation(
  current: SeparationProgress[] | undefined,
  optimistic: SeparationProgress,
): SeparationProgress[] {
  return [
    optimistic,
    ...(current ?? []).filter((item) => item.id !== optimistic.id),
  ];
}

function replaceOptimisticSeparation(
  current: SeparationProgress[] | undefined,
  optimisticSeparationId: number,
  next: SeparationProgress,
): SeparationProgress[] {
  const list = current ?? [];
  const replaced = list.map((item) =>
    item.id === optimisticSeparationId ? next : item,
  );
  if (replaced.some((item) => item.id === optimisticSeparationId)) {
    return replaced;
  }
  if (replaced.some((item) => item.id === next.id)) {
    return replaced;
  }
  return [next, ...replaced];
}

function removeOptimisticSeparation(
  current: SeparationProgress[] | undefined,
  optimisticSeparationId: number,
): SeparationProgress[] {
  return (current ?? []).filter((item) => item.id !== optimisticSeparationId);
}

export function useUploadAndSeparate() {
  const qc = useQueryClient();
  const beginPendingCreditReservation = useAuthStore(
    (state) => state.beginPendingCreditReservation,
  );
  const resolvePendingCreditReservation = useAuthStore(
    (state) => state.resolvePendingCreditReservation,
  );
  const rejectPendingCreditReservation = useAuthStore(
    (state) => state.rejectPendingCreditReservation,
  );

  return useMutation<
    SeparationProgress,
    Error,
    UploadAndSeparateArgs,
    UploadAndSeparateContext
  >({
    onMutate: ({ file }) => {
      void qc.cancelQueries({ queryKey: qk.list() });
      const pendingReservationId = createPendingReservationId();
      const optimisticSeparation = buildOptimisticSeparation(file.name);
      beginPendingCreditReservation(pendingReservationId);
      qc.setQueryData<SeparationProgress[]>(
        qk.list(),
        (current) => prependOptimisticSeparation(current, optimisticSeparation),
      );
      return {
        pendingReservationId,
        optimisticSeparationId: optimisticSeparation.id,
      };
    },
    mutationFn: async ({ file, engine, stems, guitarMode }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'options',
        JSON.stringify({ engine, stems, guitar_mode: guitarMode }),
      );
      return http<SeparationProgress>('/separation/upload', {
        method: 'POST',
        body: formData,
      });
    },
    onSuccess: (data, _variables, context) => {
      if (context) {
        resolvePendingCreditReservation(context.pendingReservationId, data.id);
        qc.setQueryData<SeparationProgress[]>(
          qk.list(),
          (current) =>
            replaceOptimisticSeparation(
              current,
              context.optimisticSeparationId,
              data,
            ),
        );
      }
      qc.invalidateQueries({ queryKey: qk.list() });
    },
    onError: (_error, _variables, context) => {
      if (context) {
        rejectPendingCreditReservation(context.pendingReservationId);
        qc.setQueryData<SeparationProgress[]>(
          qk.list(),
          (current) =>
            removeOptimisticSeparation(
              current,
              context.optimisticSeparationId,
            ),
        );
      }
    },
  });
}

export function useStartYouTubeSeparation() {
  const qc = useQueryClient();
  const beginPendingCreditReservation = useAuthStore(
    (state) => state.beginPendingCreditReservation,
  );
  const resolvePendingCreditReservation = useAuthStore(
    (state) => state.resolvePendingCreditReservation,
  );
  const rejectPendingCreditReservation = useAuthStore(
    (state) => state.rejectPendingCreditReservation,
  );

  return useMutation<
    SeparationProgress,
    Error,
    StartYouTubeSeparationArgs,
    UploadAndSeparateContext
  >({
    onMutate: ({ url }) => {
      void qc.cancelQueries({ queryKey: qk.list() });
      const pendingReservationId = createPendingReservationId();
      const optimisticSeparation = buildOptimisticSeparation(url);
      beginPendingCreditReservation(pendingReservationId);
      qc.setQueryData<SeparationProgress[]>(
        qk.list(),
        (current) => prependOptimisticSeparation(current, optimisticSeparation),
      );
      return {
        pendingReservationId,
        optimisticSeparationId: optimisticSeparation.id,
      };
    },
    mutationFn: async ({ url, engine, stems, guitarMode }) => {
      return httpJson.post<SeparationProgress>('/separation/youtube', {
        url,
        engine,
        stems,
        guitar_mode: guitarMode,
      });
    },
    onSuccess: (data, _variables, context) => {
      if (context) {
        resolvePendingCreditReservation(context.pendingReservationId, data.id);
        qc.setQueryData<SeparationProgress[]>(
          qk.list(),
          (current) =>
            replaceOptimisticSeparation(
              current,
              context.optimisticSeparationId,
              data,
            ),
        );
      }
      qc.invalidateQueries({ queryKey: qk.list() });
    },
    onError: (_error, _variables, context) => {
      if (context) {
        rejectPendingCreditReservation(context.pendingReservationId);
        qc.setQueryData<SeparationProgress[]>(
          qk.list(),
          (current) =>
            removeOptimisticSeparation(
              current,
              context.optimisticSeparationId,
            ),
        );
      }
    },
  });
}

export function useDeleteSeparation() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await http(`/api/separation/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.list() });
    },
  });
}

export function useCreateShareLink() {
  const qc = useQueryClient();
  return useMutation<
    SeparationShareStatus,
    Error,
    { sepId: number; expiresInHours: number; sharedState: SharedStudioState }
  >({
    mutationFn: async ({ sepId, expiresInHours, sharedState }) =>
      httpJson.post<SeparationShareStatus>(`/api/separation/${sepId}/share`, {
        expires_in_hours: expiresInHours,
        shared_state: sharedState,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.share(vars.sepId) });
      qc.invalidateQueries({ queryKey: qk.sharedState(vars.sepId) });
    },
  });
}

export function useUpdateShareLink() {
  const qc = useQueryClient();
  return useMutation<
    SeparationShareStatus,
    Error,
    {
      sepId: number;
      isActive?: boolean;
      expiresInHours?: number;
      sharedState?: SharedStudioState;
    }
  >({
    mutationFn: async ({ sepId, isActive, expiresInHours, sharedState }) =>
      httpJson.patch<SeparationShareStatus>(`/api/separation/${sepId}/share`, {
        is_active: isActive,
        expires_in_hours: expiresInHours,
        shared_state: sharedState,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.share(vars.sepId) });
      qc.invalidateQueries({ queryKey: qk.sharedState(vars.sepId) });
    },
  });
}

export function useSaveSharedState() {
  const qc = useQueryClient();
  return useMutation<SharedStudioState, Error, { sepId: number; state: SharedStudioState }>({
    mutationFn: async ({ sepId, state }) =>
      httpJson.put<SharedStudioState>(`/api/separation/${sepId}/shared-state`, state),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.sharedState(vars.sepId) });
      // An older GET must not overwrite this ACK. Start a fresh read without
      // awaiting it: its query function waits for this autosave to settle.
      const shareQuery = { queryKey: qk.share(vars.sepId), exact: true };
      void qc.cancelQueries(shareQuery);
      void qc.invalidateQueries(shareQuery);
    },
  });
}

export function useGenerateTab() {
  const qc = useQueryClient();
  return useMutation<TabDetail, Error, { sepId: number; stemName: string }>({
    mutationFn: async ({ sepId, stemName }) => {
      return httpJson.post<TabDetail>(`/api/separation/${sepId}/tab`, {
        stem_name: stemName,
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.tabs(vars.sepId) });
    },
  });
}

export interface ImportScoreFileArgs {
  sepId: number;
  file: File;
}

export function useImportScoreFile() {
  const qc = useQueryClient();
  return useMutation<TabDetail, Error, ImportScoreFileArgs>({
    mutationFn: async ({ sepId, file }) => {
      const formData = new FormData();
      formData.append('file', file);
      return http<TabDetail>(`/separation/${sepId}/tab/import-score-image`, {
        method: 'POST',
        body: formData,
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.tabs(vars.sepId) });
    },
  });
}

export function useDeleteTab() {
  const qc = useQueryClient();
  return useMutation<void, Error, { sepId: number; tabId: number }>({
    mutationFn: async ({ sepId, tabId }) => {
      await http(`/api/separation/${sepId}/tab/${tabId}`, { method: 'DELETE' });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.tabs(vars.sepId) });
    },
  });
}

export interface UploadTakeArgs {
  blob: Blob;
  separationId: number;
  startTime: number;
  endTime: number;
  isLoop: boolean;
}

export function useUploadTake() {
  const qc = useQueryClient();
  return useMutation<RecordingTake, Error, UploadTakeArgs>({
    mutationFn: async ({ blob, separationId, startTime, endTime, isLoop }) => {
      const formData = new FormData();
      formData.append('file', blob, 'recording.wav');
      formData.append('separation_id', String(separationId));
      formData.append('start_time', startTime.toString());
      formData.append('end_time', endTime.toString());
      formData.append('is_loop', isLoop.toString());
      return http<RecordingTake>('/recording/upload', {
        method: 'POST',
        body: formData,
      });
    },
    onSuccess: (take, vars) => {
      qc.setQueryData<RecordingTake[]>(qk.takes(vars.separationId), (previous = []) =>
        [...previous.filter((item) => item.id !== take.id), take]);
      qc.invalidateQueries({ queryKey: qk.takes(vars.separationId) });
    },
  });
}

export function useDeleteTake() {
  const qc = useQueryClient();
  return useMutation<void, Error, { takeId: number; separationId: number }>({
    mutationFn: async ({ takeId }) => {
      await http(`/api/recording/takes/${takeId}`, { method: 'DELETE' });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.takes(vars.separationId) });
    },
  });
}

export function useRecomputeChords() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean; chord_status: string; published_chord_revision_id?: number | null }, Error, { sepId: number }>({
    mutationFn: ({ sepId }) => httpJson.post(`/api/separation/${sepId}/chords/recompute`, {}),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: qk.analysis(variables.sepId) });
      queryClient.invalidateQueries({ queryKey: qk.chordRevisions(variables.sepId) });
    },
  });
}

export function useGenerateLyricAnnotations() {
  const queryClient = useQueryClient();
  return useMutation<LyricAnnotationGenerateResponse, Error, { sepId: number }>({
    mutationFn: ({ sepId }) =>
      httpJson.post<LyricAnnotationGenerateResponse>(`/api/separation/${sepId}/lyric-annotations/generate`, {}),
    onSettled: (_data, _error, variables) =>
      queryClient.invalidateQueries({ queryKey: qk.analysis(variables.sepId) }),
  });
}

export function useRetryLyricsCorrection() {
  const queryClient = useQueryClient();
  return useMutation<LyricsCorrectionRetryResponse, Error, { sepId: number }>({
    mutationFn: ({ sepId }) =>
      httpJson.post<LyricsCorrectionRetryResponse>(`/api/separation/${sepId}/lyrics/retry`, {}),
    onSuccess: (data, { sepId }) => {
      queryClient.setQueryData<AnalysisData>(qk.analysis(sepId), (current) => current
        ? { ...current, lyrics_correction_status: data.lyrics_correction_status }
        : current);
    },
    onSettled: (_data, _error, variables) =>
      queryClient.invalidateQueries({ queryKey: qk.analysis(variables.sepId) }),
  });
}

export function usePatchChordRevision() {
  const queryClient = useQueryClient();
  return useMutation<
    ChordRevision,
    Error,
    { sepId: number; revisionId: number; operations: ChordRevisionOperation[] }
  >({
    mutationFn: ({ sepId, revisionId, operations }) =>
      httpJson.patch<ChordRevision>(`/api/separation/${sepId}/chords/revisions/${revisionId}`, {
        operations,
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ChordRevision[] | undefined>(
        qk.chordRevisions(variables.sepId),
        (current) => {
          const next = current ? [...current] : [];
          const index = next.findIndex((item) => item.id === data.id);
          if (index >= 0) {
            next[index] = data;
          } else {
            next.unshift(data);
          }
          return next;
        },
      );
    },
  });
}

export function usePublishChordRevision() {
  const queryClient = useQueryClient();
  return useMutation<ChordRevision, Error, { sepId: number; revisionId: number }>({
    mutationFn: ({ sepId, revisionId }) =>
      httpJson.post<ChordRevision>(`/api/separation/${sepId}/chords/revisions/${revisionId}/publish`, {}),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: qk.analysis(variables.sepId) });
      queryClient.invalidateQueries({ queryKey: qk.chordRevisions(variables.sepId) });
    },
  });
}

export interface ExportMixdownArgs {
  sepId: number;
  tracks: Record<string, { volume: number; pan: number; mute: boolean }>;
  format: 'mp3' | 'wav';
  bpmRatio: number;
  pitchSemitones: number;
  fileName: string;
}

export async function exportMixdown(args: ExportMixdownArgs): Promise<void> {
  const res = await fetch(`/api/separation/export/${args.sepId}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tracks: args.tracks,
      format: args.format,
      bpm_ratio: args.bpmRatio,
      pitch_semitones: args.pitchSemitones,
    }),
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${args.fileName.replace(/\.[^.]+$/, '')}-mixdown.${args.format}`;
  a.click();
  URL.revokeObjectURL(url);
}
